import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  formatDateTime,
  directionLabel,
  transferStatusLabel,
  adjustmentStatusLabel,
  humanizeTreasuryError,
} from './treasuryUi';

type Tab = 'pending' | 'open' | 'arqueos' | 'diffs' | 'transfers';

function personName(u?: { firstName?: string; lastName?: string } | null) {
  if (!u) return '—';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
}

/**
 * TX7 — Supervisión de caja: único lugar para arqueos pendientes y lectura histórica.
 */
export function TreasurySupervisionPage() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const { can, session: permSession } = usePermissions();
  const canWrite = can('cash', 'write');
  const canAudit = ['ADMIN', 'MANAGER', 'ACCOUNTANT', 'AUDITOR'].includes(
    permSession?.user.role ?? '',
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'pending';

  const [pending, setPending] = useState<any[]>([]);
  const [openSessions, setOpenSessions] = useState<any[]>([]);
  const [arqueos, setArqueos] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [cashBank, setCashBank] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [filterFrom, setFilterFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [filterTo, setFilterTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [filterBranch, setFilterBranch] = useState('');
  const [filterDiffOnly, setFilterDiffOnly] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const [auditForms, setAuditForms] = useState<Record<string, { counted: string; notes: string }>>(
    {},
  );

  const setTab = (t: Tab) => setSearchParams({ tab: t });

  const load = useCallback(async () => {
    if (!legalEntityId) return;
    setLoading(true);
    setError('');
    try {
      const [pend, opens, audits, adj, tr, cbt, br] = await Promise.all([
        apiService.listPendingAuditSessions().catch(() => []),
        apiService.listOpenCashSessions().catch(() => []),
        apiService
          .getArqueosReport({
            from: filterFrom,
            to: filterTo,
            ...(filterBranch ? { branchId: filterBranch } : {}),
          })
          .catch(() => []),
        apiService.listTreasuryAdjustments(legalEntityId).catch(() => []),
        apiService.listTreasuryTransfers(legalEntityId).catch(() => []),
        apiService.listCashBankTransfers(legalEntityId).catch(() => []),
        apiService.getBranches().catch(() => []),
      ]);
      setPending(pend ?? []);
      const openFiltered = (opens ?? []).filter(
        (s: any) =>
          !legalEntityId ||
          s.cashRegister?.legalEntityId === legalEntityId ||
          s.legalEntityId === legalEntityId,
      );
      setOpenSessions(openFiltered);
      setArqueos(audits ?? []);
      setAdjustments(adj ?? []);
      setTransfers((tr ?? []).filter((t: any) => t.status === 'PENDING'));
      setCashBank((cbt ?? []).filter((t: any) => t.status === 'PENDING'));
      setBranches(br ?? []);
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [legalEntityId, filterFrom, filterTo, filterBranch]);

  useEffect(() => {
    void load();
  }, [load]);

  const staleOpens = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return openSessions.filter((s) => new Date(s.openedAt) < start);
  }, [openSessions]);

  const arqueosWithDiff = useMemo(
    () => arqueos.filter((a) => Math.abs(Number(a.difference ?? 0)) >= 0.01),
    [arqueos],
  );

  const pendingAdj = useMemo(
    () => adjustments.filter((a) => a.status === 'PENDING'),
    [adjustments],
  );

  const filteredArqueos = useMemo(() => {
    let rows = arqueos;
    if (filterDiffOnly) rows = rows.filter((a) => Math.abs(Number(a.difference ?? 0)) >= 0.01);
    if (filterStatus === 'diff') {
      rows = rows.filter((a) => Math.abs(Number(a.difference ?? 0)) >= 0.01);
    } else if (filterStatus === 'ok') {
      rows = rows.filter((a) => Math.abs(Number(a.difference ?? 0)) < 0.01);
    }
    return rows;
  }, [arqueos, filterDiffOnly, filterStatus]);

  const submitAudit = async (sessionId: string) => {
    const form = auditForms[sessionId] ?? { counted: '', notes: '' };
    const amount = parseFloat(form.counted);
    if (!Number.isFinite(amount) || amount < 0) {
      setError('Ingresá el efectivo contado para el arqueo.');
      return;
    }
    setBusyId(sessionId);
    setError('');
    try {
      await apiService.auditCashSession(sessionId, {
        countedAmount: amount,
        notes: form.notes.trim() || undefined,
      });
      setAuditForms((f) => {
        const next = { ...f };
        delete next[sessionId];
        return next;
      });
      await load();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusyId(null);
    }
  };

  const cards = [
    {
      id: 'open',
      label: 'Cajas abiertas',
      value: openSessions.length,
      hint: staleOpens.length ? `${staleOpens.length} de día anterior` : undefined,
      tab: 'open' as Tab,
    },
    {
      id: 'pending',
      label: 'Cierres pendientes de arqueo',
      value: pending.length,
      tab: 'pending' as Tab,
    },
    {
      id: 'diff',
      label: 'Arqueos con diferencia',
      value: arqueosWithDiff.length,
      tab: 'arqueos' as Tab,
    },
    {
      id: 'stale',
      label: 'Sesiones antiguas',
      value: staleOpens.length,
      tab: 'open' as Tab,
    },
    {
      id: 'xfer',
      label: 'Transferencias pendientes',
      value: transfers.length + cashBank.length + pendingAdj.length,
      tab: 'transfers' as Tab,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Supervisión</h2>
        <p className="text-sm text-gray-600 mt-1">
          Arqueos pendientes y control de sesiones. Los informes históricos son solo lectura; no
          inicies arqueos desde otros módulos.
        </p>
      </div>

      {!legalEntityId && (
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
          Seleccioná una empresa en la barra de contexto.
        </p>
      )}
      {error && (
        <p className="text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">
          {error}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setTab(c.tab)}
            className={`text-left border rounded-lg p-3 bg-white hover:bg-slate-50 ${
              tab === c.tab ? 'ring-2 ring-slate-900' : ''
            }`}
          >
            <div className="text-xs text-slate-500 uppercase tracking-wide">{c.label}</div>
            <div className="text-2xl font-semibold tabular-nums mt-1">{c.value}</div>
            {c.hint && <div className="text-xs text-amber-700 mt-1">{c.hint}</div>}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-2">
        {(
          [
            ['pending', 'Arqueos pendientes'],
            ['open', 'Sesiones abiertas'],
            ['arqueos', 'Arqueos'],
            ['diffs', 'Sobrantes y faltantes'],
            ['transfers', 'Transferencias'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              tab === id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="ml-auto px-3 py-1.5 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Actualizar
        </button>
      </div>

      {tab === 'pending' && (
        <section className="space-y-3">
          <h3 className="font-medium text-slate-900">Arqueos pendientes</h3>
          {pending.length === 0 ? (
            <p className="text-sm text-slate-500">No hay cierres pendientes de arqueo.</p>
          ) : (
            <ul className="space-y-3">
              {pending.map((s) => {
                const form = auditForms[s.id] ?? {
                  counted: String(s.closingBalance ?? s.actualBalance ?? ''),
                  notes: '',
                };
                const sys = Number(s.expectedBalance ?? s.systemBalance ?? s.closingBalance ?? 0);
                const counted = parseFloat(form.counted);
                const diff =
                  form.counted !== '' && !Number.isNaN(counted)
                    ? Math.round((counted - sys) * 100) / 100
                    : null;
                return (
                  <li key={s.id} className="border rounded-lg bg-white p-4 space-y-3">
                    <div className="flex flex-wrap justify-between gap-2 text-sm">
                      <div>
                        <div className="font-medium">
                          {s.cashRegister?.branch?.name} · Caja{' '}
                          {s.cashRegister?.number ?? s.cashRegister?.code}
                        </div>
                        <div className="text-slate-500">
                          Cajero: {personName(s.user)} · Cerrada{' '}
                          {s.closedAt ? formatDateTime(s.closedAt) : '—'}
                        </div>
                      </div>
                      <div className="text-right tabular-nums">
                        <div>Sistema / cierre: {formatMoney(sys)}</div>
                        {s.difference != null && (
                          <div
                            className={
                              Math.abs(Number(s.difference)) >= 0.01
                                ? 'text-amber-700'
                                : 'text-green-700'
                            }
                          >
                            Diff cierre: {formatMoney(Number(s.difference))}
                          </div>
                        )}
                      </div>
                    </div>
                    {canAudit ? (
                      <div className="grid md:grid-cols-3 gap-2 items-end">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Contado</label>
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            value={form.counted}
                            onChange={(e) =>
                              setAuditForms({
                                ...auditForms,
                                [s.id]: { ...form, counted: e.target.value },
                              })
                            }
                            className="w-full border rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">
                            Notas {diff != null && Math.abs(diff) >= 0.01 ? '(recomendado)' : ''}
                          </label>
                          <input
                            value={form.notes}
                            onChange={(e) =>
                              setAuditForms({
                                ...auditForms,
                                [s.id]: { ...form, notes: e.target.value },
                              })
                            }
                            className="w-full border rounded-md px-3 py-2 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={busyId === s.id}
                          onClick={() => void submitAudit(s.id)}
                          className="px-3 py-2 bg-slate-900 text-white rounded-md text-sm disabled:opacity-50"
                        >
                          {busyId === s.id ? 'Guardando…' : 'Registrar arqueo'}
                        </button>
                        {diff != null && (
                          <p className="md:col-span-3 text-xs text-slate-600">
                            Diferencia arqueo: {formatMoney(diff)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Sin permiso de arqueo.</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {tab === 'open' && (
        <section className="space-y-3">
          <h3 className="font-medium text-slate-900">Sesiones abiertas</h3>
          <div className="overflow-x-auto border rounded-lg bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Sucursal</th>
                  <th className="px-3 py-2">Caja</th>
                  <th className="px-3 py-2">Operador</th>
                  <th className="px-3 py-2">Abierta desde</th>
                  <th className="px-3 py-2">Saldo</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {openSessions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      No hay sesiones abiertas.
                    </td>
                  </tr>
                )}
                {openSessions.map((s) => {
                  const stale = new Date(s.openedAt) < new Date(new Date().setHours(0, 0, 0, 0));
                  return (
                    <tr key={s.id} className="border-t">
                      <td className="px-3 py-2">{s.cashRegister?.branch?.name ?? '—'}</td>
                      <td className="px-3 py-2">
                        {s.cashRegister?.number ?? s.cashRegister?.code ?? '—'}
                      </td>
                      <td className="px-3 py-2">{personName(s.user)}</td>
                      <td className="px-3 py-2">
                        {s.openedAt ? formatDateTime(s.openedAt) : '—'}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {s.systemBalance != null
                          ? formatMoney(Number(s.systemBalance))
                          : s.openingBalance != null
                            ? formatMoney(Number(s.openingBalance))
                            : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {stale ? (
                          <span className="text-amber-800 font-medium">Día anterior</span>
                        ) : (
                          <span className="text-green-700">Abierta</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canWrite && (
            <p className="text-xs text-slate-500">
              Para cerrar una sesión ajena usá permisos de supervisor sobre esa caja, o pedile al
              cajero que cierre desde{' '}
              <Link to="/cash" className="text-blue-700 hover:underline">
                Mi caja
              </Link>
              .
            </p>
          )}
        </section>
      )}

      {tab === 'arqueos' && (
        <section className="space-y-3">
          <h3 className="font-medium text-slate-900">Arqueos (histórico)</h3>
          <p className="text-xs text-slate-500">Solo lectura. Filtros de consulta.</p>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Desde</label>
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Hasta</label>
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Sucursal</label>
              <select
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              >
                <option value="">Todas</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Estado</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              >
                <option value="">Todos</option>
                <option value="ok">Sin diferencia</option>
                <option value="diff">Con diferencia</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm pb-1">
              <input
                type="checkbox"
                checked={filterDiffOnly}
                onChange={(e) => setFilterDiffOnly(e.target.checked)}
              />
              Solo con diferencia
            </label>
          </div>
          <div className="overflow-x-auto border rounded-lg bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Sucursal</th>
                  <th className="px-3 py-2">Caja</th>
                  <th className="px-3 py-2">Cajero</th>
                  <th className="px-3 py-2">Saldo sistema</th>
                  <th className="px-3 py-2">Contado</th>
                  <th className="px-3 py-2">Diferencia</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Supervisor</th>
                </tr>
              </thead>
              <tbody>
                {filteredArqueos.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Sin arqueos en el rango.
                    </td>
                  </tr>
                )}
                {filteredArqueos.map((a) => {
                  const diff = Number(a.difference ?? 0);
                  const status =
                    Math.abs(diff) < 0.01 ? 'OK' : diff > 0 ? 'Sobrante' : 'Faltante';
                  return (
                    <tr key={a.id} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {a.auditedAt ? formatDateTime(a.auditedAt) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {a.session?.cashRegister?.branch?.name ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        {a.session?.cashRegister?.number ?? a.session?.cashRegister?.code ?? '—'}
                      </td>
                      <td className="px-3 py-2">{personName(a.session?.user)}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {formatMoney(Number(a.systemBalance ?? a.expectedAmount ?? 0))}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {formatMoney(Number(a.countedAmount ?? 0))}
                      </td>
                      <td
                        className={`px-3 py-2 tabular-nums ${
                          Math.abs(diff) >= 0.01 ? 'text-amber-800 font-medium' : ''
                        }`}
                      >
                        {formatMoney(diff)}
                      </td>
                      <td className="px-3 py-2">{status}</td>
                      <td className="px-3 py-2">{personName(a.auditedBy)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'diffs' && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-slate-900">Sobrantes y faltantes</h3>
              <p className="text-xs text-slate-500">
                Ajustes de caja. Estados: Pendiente · Aprobado · Rechazado.
              </p>
            </div>
            <Link to="/treasury/config" className="text-sm text-blue-700 hover:underline">
              Solicitar en Configuración
            </Link>
          </div>
          <div className="overflow-x-auto border rounded-lg bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Caja</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Monto</th>
                  <th className="px-3 py-2">Motivo</th>
                  <th className="px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      Sin registros.
                    </td>
                  </tr>
                )}
                {adjustments.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="px-3 py-2">
                      {formatDateTime(a.createdAt || a.requestedAt)}
                    </td>
                    <td className="px-3 py-2">{a.cashRegister?.code ?? a.cashRegisterId}</td>
                    <td className="px-3 py-2">
                      {directionLabel(a.direction)}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{formatMoney(Number(a.amount))}</td>
                    <td className="px-3 py-2 max-w-[12rem] truncate" title={a.reason}>
                      {a.reason || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          a.status === 'PENDING'
                            ? 'text-amber-800'
                            : a.status === 'APPROVED' || a.status === 'COMPLETED'
                              ? 'text-green-700'
                              : 'text-red-700'
                        }
                      >
{adjustmentStatusLabel(a.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'transfers' && (
        <section className="space-y-4">
          <div>
            <h3 className="font-medium text-slate-900">Transferencias pendientes</h3>
            <Link to="/treasury/approvals" className="text-sm text-blue-700 hover:underline">
              Ir a Aprobaciones
            </Link>
          </div>
          <ul className="divide-y border rounded-lg bg-white text-sm">
            {transfers.length === 0 && cashBank.length === 0 && pendingAdj.length === 0 && (
              <li className="px-4 py-4 text-slate-500">No hay pendientes.</li>
            )}
            {transfers.map((t) => (
              <li key={t.id} className="px-4 py-3 flex flex-wrap justify-between gap-2">
                <span>
                  Estructural · {formatMoney(Number(t.amount))} · {t.fromRegister?.code} →{' '}
                  {t.toRegister?.code}
                </span>
                <span className="text-amber-800">{transferStatusLabel('PENDING')}</span>
              </li>
            ))}
            {cashBank.map((t) => (
              <li key={t.id} className="px-4 py-3 flex flex-wrap justify-between gap-2">
                <span>
                  {directionLabel(t.direction)} ·{' '}
                  {formatMoney(Number(t.amount))} · {t.cashRegister?.code}
                </span>
                <span className="text-amber-800">{transferStatusLabel('PENDING')}</span>
              </li>
            ))}
            {pendingAdj.map((a) => (
              <li key={a.id} className="px-4 py-3 flex flex-wrap justify-between gap-2">
                <span>
                  {directionLabel(a.direction)} · {formatMoney(Number(a.amount))}
                </span>
                <span className="text-amber-800">{transferStatusLabel('PENDING')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
